import type { ReactNode } from 'react';
import {
  useLeadProjects,
  usePracticeUsers,
  useTasksFromProjectIdArray,
} from '@witmetrics/data-store';
import {
  filterUnisonTasks,
  getTaskFilterCount,
} from '@witmetrics/utils/filters';
import { mapToId, mapToKey } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useIds } from '@/hooks/useIds';
import { Tab } from '@/components/TabsBlock';
import Section from '@/overlays/CaseOverlay/Section';
import SectionTitle from '@/overlays/CaseOverlay/SectionTitle';
import UnisonProjectTasksList from '@/components/UnisonProjectTasksList';
import TasksFilterMenu from '@/menus/TasksFilterMenu';
import EmptyMessage from '@/overlays/CaseOverlay/EmptyMessage';
import StatusToggle from './StatusToggle';
import { classes } from './utils';

export type TasksTabProps = {
  isFetchingData: boolean;
  name: string;
  label: string;
  adornment?: ReactNode;
};

export default function TasksTab({
  isFetchingData,
  name,
  label,
  adornment,
}: TasksTabProps) {
  const { activeLeadId } = useAppState();
  const practiceUsers = usePracticeUsers();
  const projects = useLeadProjects(activeLeadId);
  const tasks = useTasksFromProjectIdArray(mapToId(projects));
  const [userIds, setUserIds] = useIds(mapToKey(practiceUsers, 'userId'));
  const [showAll, toggleShowAll] = useToggle();

  const getFiltersCount = () => {
    return getTaskFilterCount([], userIds, practiceUsers);
  };

  const renderTasks = () => {
    if (!isFetchingData && isEmpty(tasks)) {
      return <EmptyMessage>No tasks for current patient.</EmptyMessage>;
    }
    let filteredTasks = filterUnisonTasks(tasks, [], userIds);
    if (!showAll) {
      // Only show unresolved tasks
      filteredTasks = filteredTasks.filter(({ task }) => !task.isResolved);
    }
    if (!isFetchingData && isEmpty(filteredTasks)) {
      return <EmptyMessage>No tasks matching current filters.</EmptyMessage>;
    }
    return (
      <UnisonProjectTasksList
        isFetchingData={isFetchingData}
        className={classes.list}
        unisonProjectTasks={filteredTasks}
      />
    );
  };

  if (!activeLeadId) return null;

  return (
    <Tab
      className={classes.tab}
      name={name}
      label={label}
      adornment={adornment}>
      <Section>
        <SectionTitle
          title="Tasks"
          filtersCount={getFiltersCount()}
          renderControls={() => (
            <StatusToggle
              className={classes.toggle}
              optionClassName={classes.toggleOption}
              value={showAll}
              onChange={toggleShowAll}
            />
          )}
          renderFilterMenu={(props) => (
            <TasksFilterMenu
              userFilters={userIds}
              onApplyFilters={(update) => setUserIds(update.userFilters)}
              {...props}
            />
          )}
        />
        {renderTasks()}
      </Section>
    </Tab>
  );
}
